import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  Typography,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Checkbox,
  ListItemText,
} from '@mui/material';
import { Add, Edit, Delete, LockReset } from '@mui/icons-material';
import api from '../../services/authService';
import { useNotification } from '../../contexts/NotificationContext';
import { exportToExcel, exportToPDF } from '../../utils/exportHelpers';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { extractAndTranslateError, formatNumber, formatCurrency, formatPhoneNumber } from '../../utils/i18nHelpers';

interface Technician {
  id: number;
  user_id?: number | null;
  user?: { id: number; name: string; name_ar?: string; email: string } | null;
  phone?: string;
  service_type: string; // Keep for backward compatibility
  service_types?: Array<{ id: number; name_en: string; name_ar: string; pivot?: { created_at: string; updated_at: string } }>;
  specialization?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

const Technicians: React.FC = () => {
  const { notifySuccess, notifyError } = useNotification();
  const { can } = useAuth();
  const { t, language } = useLanguage();
  const [technicians, setTechnicians] = useState<Technician[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingTechnician, setEditingTechnician] = useState<Technician | null>(null);
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [passwordForUserId, setPasswordForUserId] = useState<number | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submittingPassword, setSubmittingPassword] = useState(false);
  const [submittingServiceType, setSubmittingServiceType] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [serviceTypes, setServiceTypes] = useState<Array<{ id: number; name_en: string; name_ar: string }>>([]);
  const [serviceTypeDialogOpen, setServiceTypeDialogOpen] = useState(false);
  const [editingServiceType, setEditingServiceType] = useState<{ id: number; name_en: string; name_ar: string } | null>(null);
  const [newServiceType, setNewServiceType] = useState({
    name_en: '',
    name_ar: '',
  });
  const [formData, setFormData] = useState({
    phone: '',
    service_type: '', // Keep for backward compatibility
    service_types: [] as number[],
    specialization: '',
    notes: '',
    // New user creation fields when user_id is not selected
    user_name: '',
    user_name_ar: '',
    password: '',
  });

  useEffect(() => {
    fetchTechnicians();
    fetchServiceTypes();
  }, []);

  const fetchServiceTypes = async () => {
    try {
      const response = await api.get('/service-types');
      setServiceTypes(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching service types:', error);
      setServiceTypes([]);
    }
  };

  const fetchTechnicians = async () => {
    try {
      const response = await api.get('/technicians');
      setTechnicians(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching technicians:', error);
      setTechnicians([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = (technician?: Technician) => {
    if (technician) {
      setEditingTechnician(technician);
      setErrors({});
      // Get service type IDs from service_types relationship if available, otherwise use service_type string
      const serviceTypeIds = technician.service_types?.map(st => st.id) || [];
      setFormData({
        phone: technician.phone || '',
        service_type: technician.service_type || '',
        service_types: serviceTypeIds,
        specialization: technician.specialization || '',
        notes: technician.notes || '',
        user_name: technician.user?.name || '',
        user_name_ar: technician.user?.name_ar || '',
        password: '',
      });
    } else {
      setEditingTechnician(null);
      setErrors({});
      setFormData({
        phone: '',
        service_type: '',
        service_types: [],
        specialization: '',
        notes: '',
        user_name: '',
        user_name_ar: '',
        password: '',
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingTechnician(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return; // Prevent double submission
    setSubmitting(true);
    try {
      setErrors({});
      // Ensure at least one service type is selected
      if (formData.service_types.length === 0) {
        notifyError(t('technicians.messages.save.serviceTypeRequired') || 'At least one service type is required.');
        setSubmitting(false);
        return;
      }

      const submitData: any = {
        phone: formData.phone || undefined,
        service_types: formData.service_types.length > 0 ? formData.service_types : undefined,
        specialization: formData.specialization || undefined,
        notes: formData.notes || undefined,
      };
      if (!editingTechnician) {
        submitData.user_name = formData.user_name;
        submitData.user_name_ar = formData.user_name_ar || undefined;
        submitData.password = formData.password;
      }

      if (editingTechnician) {
        if (editingTechnician.user?.id) {
          const userUpdateData: any = {};
          if (formData.user_name && formData.user_name !== editingTechnician.user.name) {
            userUpdateData.name = formData.user_name;
          }
          if (formData.user_name_ar !== (editingTechnician.user.name_ar || '')) {
            userUpdateData.name_ar = formData.user_name_ar || null;
          }
          if (Object.keys(userUpdateData).length > 0) {
            await api.put(`/users/${editingTechnician.user.id}`, userUpdateData);
          }
        }
        await api.put(`/technicians/${editingTechnician.id}`, submitData);
        notifySuccess(t('technicians.messages.save.successUpdate'));
      } else {
        await api.post('/technicians', submitData);
        notifySuccess(t('technicians.messages.save.successCreate'));
      }
      await fetchTechnicians();
      handleClose();
    } catch (error: any) {
      if (error?.response?.status === 422) {
        setErrors(error.response.data?.errors || {});
      } else {
        console.error('Error saving technician:', error);
      }
      const errorMessage = extractAndTranslateError(error, language, t);
      notifyError(errorMessage || t('technicians.messages.save.errorDefault'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm(t('technicians.messages.delete.confirm'))) {
      try {
        await api.delete(`/technicians/${id}`);
        fetchTechnicians();
        notifySuccess(t('technicians.messages.delete.success'));
      } catch (error) {
        console.error('Error deleting technician:', error);
        const errorMessage = extractAndTranslateError(error, language, t);
        notifyError(errorMessage || t('technicians.messages.delete.errorDefault'));
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const getServiceTypeColor = (serviceType: string) => {
    const colors: { [key: string]: string } = {
      'plumbing': 'primary',
      'electrical': 'secondary',
      'cleaning': 'success',
      'security': 'warning',
      'maintenance': 'info',
      'repair': 'error',
      'other': 'default',
    };
    return colors[serviceType.toLowerCase()] || 'default';
  };

  if (loading) {
    return <div>{t('technicians.loading')}</div>;
  }

  const filteredTechnicians = technicians.filter((p) => {
    const term = searchTerm.toLowerCase();
    if (!term) return true;
    const serviceTypeNames = p.service_types?.map(st => {
      const serviceType = serviceTypes.find(s => s.id === st.id);
      return serviceType ? `${serviceType.name_en} ${serviceType.name_ar}` : '';
    }).join(' ') || '';
    return (
      (p.user?.name || '').toLowerCase().includes(term) ||
      ((p.user as any)?.name_ar || '').toLowerCase().includes(term) ||
      (p.user?.email || '').toLowerCase().includes(term) ||
      (p.phone || '').toLowerCase().includes(term) ||
      (p.service_type || '').toLowerCase().includes(term) ||
      (p.specialization || '').toLowerCase().includes(term) ||
      serviceTypeNames.toLowerCase().includes(term)
    );
  });

  const handleExportExcel = async () => {
    if (!filteredTechnicians.length) {
      notifyError(t('technicians.export.noData'));
      return;
    }
    const rows = filteredTechnicians.map((p) => {
      const serviceTypeNames = p.service_types && p.service_types.length > 0
        ? p.service_types.map(st => {
            const serviceType = serviceTypes.find(s => s.id === st.id);
            return serviceType ? (language === 'ar' ? serviceType.name_ar : serviceType.name_en) : st.name_en;
          }).join(', ')
        : p.service_type || '';
      return {
        User: (language === 'ar' && p.user?.name_ar ? p.user.name_ar : p.user?.name) || '',
        Phone: p.phone || '',
        ServiceTypes: serviceTypeNames,
        Specialization: p.specialization || '',
        Notes: p.notes || '',
      };
    });
    await exportToExcel(rows, 'technicians');
    notifySuccess(t('technicians.export.excel.success'));
  };

  const handleExportPDF = async () => {
    if (!filteredTechnicians.length) {
      notifyError(t('technicians.export.noData'));
      return;
    }
    const columns = [
      t('technicians.export.columns.user'),
      t('technicians.export.columns.phone'),
      t('technicians.export.columns.serviceTypes'),
      t('technicians.export.columns.specialization'),
      t('technicians.export.columns.notes')
    ];
    const data = filteredTechnicians.map((p) => {
      const serviceTypeNames = p.service_types && p.service_types.length > 0
        ? p.service_types.map(st => {
            const serviceType = serviceTypes.find(s => s.id === st.id);
            return serviceType ? (language === 'ar' ? serviceType.name_ar : serviceType.name_en) : st.name_en;
          }).join(', ')
        : p.service_type || '';
      return [
        (language === 'ar' && p.user?.name_ar ? p.user.name_ar : p.user?.name) || '',
        p.phone || '',
        serviceTypeNames,
        p.specialization || '',
        p.notes || '',
      ];
    });
    await exportToPDF(columns, data, 'technicians', language, t('technicians.title'));
    notifySuccess(t('technicians.export.pdf.success'));
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} gap={1} flexWrap="wrap">
        <Typography variant="h4">{t('technicians.title')}</Typography>
        <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
          <TextField
            size="small"
            placeholder={t('technicians.search.placeholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {can('technicians.export-excel') && (
            <Button variant="outlined" onClick={handleExportExcel}>
              {t('technicians.export.excel')}
            </Button>
          )}
          {can('technicians.export-pdf') && (
            <Button variant="outlined" onClick={handleExportPDF}>
              {t('technicians.export.pdf')}
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpen()}
          >
            {t('technicians.actions.add')}
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>{t('technicians.table.user')}</strong></TableCell>
              <TableCell><strong>{t('technicians.table.userAr')}</strong></TableCell>
              <TableCell><strong>{t('technicians.table.phone')}</strong></TableCell>
              <TableCell><strong>{t('technicians.table.serviceType')}</strong></TableCell>
              <TableCell><strong>{t('technicians.table.specialization')}</strong></TableCell>
              <TableCell><strong>{t('technicians.table.notes')}</strong></TableCell>
              <TableCell align="right"><strong>{t('technicians.table.actions')}</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredTechnicians.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="text.secondary">
                    {t('technicians.table.noData')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredTechnicians.map((technician) => (
                <TableRow key={technician.id} hover>
                  <TableCell>{technician.user?.name || '-'}</TableCell>
                  <TableCell>{technician.user?.name_ar || '-'}</TableCell>
                  <TableCell>{technician.phone ? formatPhoneNumber(technician.phone, language) : '-'}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {technician.service_types && technician.service_types.length > 0 ? (
                        technician.service_types.map((st) => {
                          const serviceType = serviceTypes.find(s => s.id === st.id);
                          const displayName = serviceType ? (language === 'ar' ? serviceType.name_ar : serviceType.name_en) : st.name_en;
                          return (
                            <Chip
                              key={st.id}
                              label={displayName}
                              size="small"
                              color={getServiceTypeColor(st.name_en) as any}
                            />
                          );
                        })
                      ) : technician.service_type ? (
                        <Chip
                          label={(() => {
                            const serviceType = serviceTypes.find(st => st.name_en === technician.service_type);
                            return serviceType ? (language === 'ar' ? serviceType.name_ar : serviceType.name_en) : technician.service_type.toUpperCase();
                          })()}
                          size="small"
                          color={getServiceTypeColor(technician.service_type) as any}
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">-</Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{technician.specialization || '-'}</TableCell>
                  <TableCell>
                    {technician.notes ? (
                      <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {technician.notes}
                      </Typography>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleOpen(technician)}
                      title={t('technicians.actions.edit')}
                    >
                      <Edit />
                    </IconButton>
                    {technician.user?.id && (
                      <IconButton
                        size="small"
                        onClick={() => {
                          setPasswordForUserId(technician.user!.id);
                          setNewPassword('');
                          setPasswordError(null);
                          setPasswordOpen(true);
                        }}
                        title={t('technicians.actions.changePassword')}
                      >
                        <LockReset />
                      </IconButton>
                    )}
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(technician.id)}
                      color="error"
                      title={t('technicians.actions.delete')}
                    >
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingTechnician ? t('technicians.dialog.editTitle') : t('technicians.dialog.addTitle')}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                fullWidth
                label={t('technicians.dialog.phone')}
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                required
                error={!!errors.phone}
                helperText={errors.phone?.[0]}
              />
              {!editingTechnician && (
                <>
                  <TextField
                    fullWidth
                    label={t('technicians.dialog.fullName') || 'Full Name'}
                    name="user_name"
                    value={formData.user_name}
                    onChange={handleInputChange}
                    required
                    error={!!errors.user_name}
                    helperText={errors.user_name?.[0]}
                  />
                  <TextField
                    fullWidth
                    label={t('technicians.dialog.fullNameAr') || 'Full Name (Arabic)'}
                    name="user_name_ar"
                    value={formData.user_name_ar}
                    onChange={handleInputChange}
                    error={!!errors.user_name_ar}
                    helperText={errors.user_name_ar?.[0]}
                  />
                  <TextField
                    fullWidth
                    label={t('technicians.dialog.password')}
                    name="password"
                    type="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    required
                    error={!!errors.password}
                    helperText={errors.password?.[0]}
                  />
                </>
              )}
              {editingTechnician && (
                <>
                  <TextField
                    fullWidth
                    label={t('technicians.dialog.fullName') || 'Full Name'}
                    name="user_name"
                    value={formData.user_name}
                    onChange={handleInputChange}
                    error={!!errors.user_name}
                    helperText={errors.user_name?.[0]}
                  />
                  <TextField
                    fullWidth
                    label={t('technicians.dialog.fullNameAr') || 'Full Name (Arabic)'}
                    name="user_name_ar"
                    value={formData.user_name_ar}
                    onChange={handleInputChange}
                    error={!!errors.user_name_ar}
                    helperText={errors.user_name_ar?.[0]}
                  />
                </>
              )}
              
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <FormControl sx={{ flex: 1, minWidth: 200 }} error={!!errors.service_types || !!errors.service_type}>
                  <InputLabel>{t('technicians.dialog.serviceTypes') || t('technicians.dialog.serviceType')}</InputLabel>
                  <Select
                    multiple
                    value={formData.service_types}
                    label={t('technicians.dialog.serviceTypes') || t('technicians.dialog.serviceType')}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Material-UI Select with multiple returns string[] or number[] based on value prop type
                      // Convert to number[] to match our formData type
                      const serviceTypeIds = typeof value === 'string' 
                        ? value.split(',').map(id => Number(id)).filter(id => !isNaN(id))
                        : (Array.isArray(value) ? value.map(id => typeof id === 'string' ? Number(id) : id).filter(id => typeof id === 'number' && !isNaN(id)) : []);
                      setFormData({ 
                        ...formData, 
                        service_types: serviceTypeIds as number[]
                      });
                    }}
                    renderValue={(selected) => {
                      const selectedIds = selected as number[];
                      const selectedTypes = serviceTypes.filter(st => selectedIds.includes(st.id));
                      return selectedTypes.map(st => language === 'ar' ? st.name_ar : st.name_en).join(', ');
                    }}
                    required
                  >
                    {serviceTypes.map((serviceType) => (
                      <MenuItem 
                        key={serviceType.id} 
                        value={serviceType.id}
                        onClick={(e) => {
                          // Only select if clicking on the text, not the buttons
                          if ((e.target as HTMLElement).closest('.MuiIconButton-root')) {
                            e.preventDefault();
                            return;
                          }
                        }}
                        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                      >
                        <Checkbox checked={formData.service_types.indexOf(serviceType.id) > -1} />
                        <ListItemText primary={language === 'ar' ? serviceType.name_ar : serviceType.name_en} />
                        {can('technicians.edit') && (
                          <Box sx={{ display: 'flex', gap: 0.5, ml: 2 }} onClick={(e) => e.stopPropagation()}>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                setEditingServiceType(serviceType);
                                setNewServiceType({ name_en: serviceType.name_en, name_ar: serviceType.name_ar });
                                setServiceTypeDialogOpen(true);
                                // Close the select menu
                                const selectElement = e.currentTarget.closest('.MuiSelect-root') as HTMLElement;
                                if (selectElement) {
                                  selectElement.blur();
                                }
                              }}
                              title={t('technicians.dialog.editServiceType') || 'Edit Service Type'}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Box>
                        )}
                      </MenuItem>
                    ))}
                  </Select>
                  {!!errors.service_type && (
                    <FormHelperText>{errors.service_type?.[0]}</FormHelperText>
                  )}
                </FormControl>
                {can('technicians.edit') && (
                  <Button
                    variant="outlined"
                    startIcon={<Add />}
                    onClick={() => {
                      setEditingServiceType(null);
                      setNewServiceType({ name_en: '', name_ar: '' });
                      setServiceTypeDialogOpen(true);
                    }}
                    sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                  >
                    {t('technicians.dialog.addServiceType') || 'Add Service Type'}
                  </Button>
                )}
                <TextField
                  sx={{ flex: 1, minWidth: 200 }}
                  label={t('technicians.dialog.specialization')}
                  name="specialization"
                  value={formData.specialization}
                  onChange={handleInputChange}
                  error={!!errors.specialization}
                  helperText={errors.specialization?.[0]}
                />
              </Box>
              
              <TextField
                fullWidth
                label={t('technicians.dialog.notes')}
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                multiline
                rows={3}
                error={!!errors.notes}
                helperText={errors.notes?.[0]}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} disabled={submitting}>{t('technicians.dialog.cancel')}</Button>
            <Button type="submit" variant="contained" disabled={submitting} startIcon={submitting ? <CircularProgress size={20} /> : null}>
              {editingTechnician ? t('technicians.dialog.update') : t('technicians.dialog.create')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
      <Dialog open={passwordOpen} onClose={() => setPasswordOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('technicians.passwordDialog.title')}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label={t('technicians.passwordDialog.newPassword')}
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            autoFocus
            required
            error={!!passwordError}
            helperText={passwordError || ''}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordOpen(false)}>
            {t('technicians.passwordDialog.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              try {
                setPasswordError(null);
                if (!passwordForUserId) return;
                await api.put(`/users/${passwordForUserId}`, { password: newPassword });
                setPasswordOpen(false);
                notifySuccess(t('technicians.messages.password.success'));
              } catch (err: any) {
                if (err?.response?.status === 422) {
                  const errs = err.response.data?.errors;
                  setPasswordError(errs?.password?.[0] || t('technicians.messages.password.errorDefault'));
                } else {
                  setPasswordError(t('technicians.messages.password.errorDefault'));
                }
                notifyError(t('technicians.messages.password.errorDefault'));
              }
            }}
          >
            {t('technicians.passwordDialog.update')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Service Type Dialog */}
      <Dialog open={serviceTypeDialogOpen} onClose={() => {
        setServiceTypeDialogOpen(false);
        setEditingServiceType(null);
        setNewServiceType({ name_en: '', name_ar: '' });
      }} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingServiceType 
            ? (t('technicians.dialog.editServiceType.title') || 'Edit Service Type')
            : (t('technicians.dialog.addServiceType.title') || 'Add Service Type')
          }
        </DialogTitle>
        <form onSubmit={async (e) => {
          e.preventDefault();
          if (submittingServiceType) return; // Prevent double submission
          setSubmittingServiceType(true);
          try {
            if (editingServiceType) {
              await api.put(`/service-types/${editingServiceType.id}`, newServiceType);
              notifySuccess(t('technicians.dialog.editServiceType.success') || 'Service type updated successfully');
            } else {
              await api.post('/service-types', newServiceType);
              notifySuccess(t('technicians.dialog.addServiceType.success') || 'Service type added successfully');
            }
            setNewServiceType({ name_en: '', name_ar: '' });
            setEditingServiceType(null);
            setServiceTypeDialogOpen(false);
            fetchServiceTypes();
          } catch (error: any) {
            console.error('Error saving service type:', error);
            const errorMessage = extractAndTranslateError(error, language, t);
            notifyError(errorMessage || (editingServiceType 
              ? (t('technicians.dialog.editServiceType.error') || 'Failed to update service type')
              : (t('technicians.dialog.addServiceType.error') || 'Failed to add service type')
            ));
          } finally {
            setSubmittingServiceType(false);
          }
        }}>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <TextField
                fullWidth
                label={t('technicians.dialog.addServiceType.nameEn') || 'Name (English)'}
                value={newServiceType.name_en}
                onChange={(e) => setNewServiceType({ ...newServiceType, name_en: e.target.value })}
                required
              />
              <TextField
                fullWidth
                label={t('technicians.dialog.addServiceType.nameAr') || 'Name (Arabic)'}
                value={newServiceType.name_ar}
                onChange={(e) => setNewServiceType({ ...newServiceType, name_ar: e.target.value })}
                required
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setServiceTypeDialogOpen(false);
              setEditingServiceType(null);
              setNewServiceType({ name_en: '', name_ar: '' });
            }} disabled={submittingServiceType}>
              {t('technicians.dialog.cancel')}
            </Button>
            <Button type="submit" variant="contained" disabled={submittingServiceType} startIcon={submittingServiceType ? <CircularProgress size={20} /> : null}>
              {editingServiceType 
                ? (t('technicians.dialog.editServiceType.update') || 'Update')
                : (t('technicians.dialog.addServiceType.add') || 'Add')
              }
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
}

export default Technicians;
